import { emailRegex } from "react-forms-state/src";
import Translations from "translations/types";
import { z } from "zod";

export type SenderLoginView = "login" | "challenge";

export type GetSenderLoginSchemaArgs = {
  isSingleSignOn: boolean;
  translations: Translations;
  view: SenderLoginView;
};

type SenderLoginSchema = ReturnType<typeof getSenderLoginSchema>;

/** Raw form state (inputs) — `challenge` stays a string in the DOM. */
export type SenderLoginFormValues = z.input<SenderLoginSchema>;

/** Post-validation shape — login view normalizes empty `challenge` to `undefined`. */
export type SenderLoginParsedValues = z.output<SenderLoginSchema>;

/** Submit payload passed to `handleLogin` — same as validated form output. */
export type SenderLoginSubmitPayload = SenderLoginParsedValues;

/** Route / container token — not a visible field; kept in form state for a single submit shape. */
const ssoTokenField = z.string().optional();

/** Login view: hidden challenge input submits as omitted when empty (legacy getLoginArguments). */
const loginViewChallengeField = z
  .string()
  .transform((value) => value || undefined);

export function getSenderLoginSchema({
  isSingleSignOn,
  translations,
  view,
}: GetSenderLoginSchemaArgs) {
  if (view === "challenge") {
    return z.object({
      challenge: z
        .string()
        .min(1, translations.login.loginPage.challengeCodeValidation),
      email: z.string(),
      password: z.string(),
      ssoToken: ssoTokenField,
    });
  }

  return z.object({
    challenge: loginViewChallengeField,
    email: z
      .string()
      .regex(emailRegex, translations.login.loginPage.emailValidation),
    password: isSingleSignOn
      ? z.string()
      : z.string().min(1, translations.login.loginPage.passwordValidation),
    ssoToken: ssoTokenField,
  });
}
